import React, { useState, useMemo, useRef } from 'react';

export interface TagTreeNode {
    tag: string;
    title: string;
    children: TagTreeNode[];
}

interface TagsTreeSelectorProps {
    tagsTree: TagTreeNode[];
    selectedPages: string[][];  // Array of tag arrays
    onChange: (selectedPages: string[][]) => void;
}

interface FlatNode {
    node: TagTreeNode;
    parentPath: string[];
    isGroupNode: boolean;
    path: string[];
}

const TagsTreeSelector: React.FC<TagsTreeSelectorProps> = ({ tagsTree, selectedPages, onChange }) => {
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [lastClickedPath, setLastClickedPath] = useState<string | null>(null);

    // Convert selectedPages to a Set for faster lookup
    const selectedPagesSet = useMemo(() => {
        return new Set(selectedPages.map(page => page.join(',')));
    }, [selectedPages]);

    // Build flat list of all selectable nodes (for Shift+click range selection)
    const flatNodes = useMemo(() => {
        const result: FlatNode[] = [];

        const traverse = (nodes: TagTreeNode[], parentPath: string[] = [], level: number = 0) => {
            for (const node of nodes) {
                const isGroupNode = level === 0;
                const currentPath = isGroupNode ? parentPath : [...parentPath, node.tag];
                const pathKey = [...parentPath, node.tag].join(',');

                if (!isGroupNode) {
                    result.push({ node, parentPath, isGroupNode, path: currentPath });
                }

                // Only include children if node is expanded
                if (expanded.has(pathKey) && node.children.length > 0) {
                    traverse(node.children, currentPath, level + 1);
                }
            }
        };

        traverse(tagsTree);
        return result;
    }, [tagsTree, expanded]);

    // Get all descendant pages for a given node (excluding group nodes at level 0)
    const getDescendantPages = (node: TagTreeNode, parentPath: string[], isGroupNode: boolean = false): string[][] => {
        const descendants: string[][] = [];

        // If this is not a group node (level 0), include it as a valid page
        if (!isGroupNode) {
            const currentPath = [...parentPath, node.tag];
            descendants.push(currentPath);

            // Add all descendants
            for (const child of node.children) {
                descendants.push(...getDescendantPages(child, currentPath, false));
            }
        } else {
            // This is a group node, so only process children without adding this node to path
            for (const child of node.children) {
                descendants.push(...getDescendantPages(child, parentPath, false));
            }
        }

        return descendants;
    };

    // Check if a page is selected
    const isPageSelected = (page: string[]): boolean => {
        return selectedPagesSet.has(page.join(','));
    };

    // Check if node is indeterminate (some children selected, not all)
    const isIndeterminate = (node: TagTreeNode, parentPath: string[], isGroupNode: boolean): boolean => {
        const descendants = getDescendantPages(node, parentPath, isGroupNode);
        if (descendants.length === 0) return false;

        const selectedCount = descendants.filter(isPageSelected).length;
        return selectedCount > 0 && selectedCount < descendants.length;
    };

    // Check if all descendants are selected
    const areAllDescendantsSelected = (node: TagTreeNode, parentPath: string[], isGroupNode: boolean): boolean => {
        const descendants = getDescendantPages(node, parentPath, isGroupNode);
        if (descendants.length === 0) return false;

        return descendants.every(isPageSelected);
    };

    // Toggle node expansion
    const toggleExpand = (path: string) => {
        const newExpanded = new Set(expanded);
        if (newExpanded.has(path)) {
            newExpanded.delete(path);
        } else {
            newExpanded.add(path);
        }
        setExpanded(newExpanded);
    };

    // Handle checkbox change with Ctrl/Shift support
    const handleCheckboxChange = (
        node: TagTreeNode,
        parentPath: string[],
        isGroupNode: boolean,
        event: React.MouseEvent<HTMLInputElement>
    ) => {
        const currentPath = isGroupNode ? parentPath : [...parentPath, node.tag];
        const currentPathKey = currentPath.join(',');

        // Ctrl/Cmd + Click: Toggle single item without affecting descendants
        if (event.ctrlKey || event.metaKey) {
            const isCurrentlySelected = isPageSelected(currentPath);

            if (isCurrentlySelected) {
                // Remove only this specific page
                const newSelected = selectedPages.filter(page => page.join(',') !== currentPathKey);
                onChange(newSelected);
            } else {
                // Add only this specific page
                onChange([...selectedPages, currentPath]);
            }
            setLastClickedPath(currentPathKey);
            return;
        }

        // Shift + Click: Select range from last clicked to current
        if (event.shiftKey && lastClickedPath) {
            const lastIndex = flatNodes.findIndex(fn => fn.path.join(',') === lastClickedPath);
            const currentIndex = flatNodes.findIndex(fn => fn.path.join(',') === currentPathKey);

            if (lastIndex !== -1 && currentIndex !== -1) {
                const start = Math.min(lastIndex, currentIndex);
                const end = Math.max(lastIndex, currentIndex);
                const rangeNodes = flatNodes.slice(start, end + 1);

                // Add all nodes in range to selection
                const newSelected = [...selectedPages];
                for (const fn of rangeNodes) {
                    const pathKey = fn.path.join(',');
                    if (!selectedPagesSet.has(pathKey)) {
                        newSelected.push(fn.path);
                    }
                }
                onChange(newSelected);
                return;
            }
        }

        // Regular click: Select/deselect node and all descendants
        const descendants = getDescendantPages(node, parentPath, isGroupNode);
        const isSelected = areAllDescendantsSelected(node, parentPath, isGroupNode);

        if (isSelected) {
            // Deselect all descendants
            const newSelected = selectedPages.filter(page => {
                const pageKey = page.join(',');
                return !descendants.some(desc => desc.join(',') === pageKey);
            });
            onChange(newSelected);
        } else {
            // Select all descendants
            const newSelected = [...selectedPages];
            for (const desc of descendants) {
                const descKey = desc.join(',');
                if (!selectedPagesSet.has(descKey)) {
                    newSelected.push(desc);
                }
            }
            onChange(newSelected);
        }

        setLastClickedPath(currentPathKey);
    };

    // Render tree node
    const renderNode = (node: TagTreeNode, parentPath: string[] = [], level: number = 0, isGroupNode: boolean = false): React.ReactNode => {
        // Level 0 nodes are group nodes (Атрибут, Страны, etc.)
        const isThisGroupNode = level === 0;

        const currentPath = isThisGroupNode ? parentPath : [...parentPath, node.tag];
        const pathKey = [...parentPath, node.tag].join(','); // For expansion tracking
        const isExpanded = expanded.has(pathKey);
        const hasChildren = node.children.length > 0;

        // Only show checkbox for non-group nodes
        const showCheckbox = !isThisGroupNode;
        const isChecked = showCheckbox && areAllDescendantsSelected(node, parentPath, isThisGroupNode);
        const isIndeterminateState = showCheckbox && isIndeterminate(node, parentPath, isThisGroupNode);

        return (
            <div key={pathKey} style={{ marginLeft: level * 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                    {hasChildren && (
                        <button
                            onClick={() => toggleExpand(pathKey)}
                            style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '0 8px',
                                fontSize: '14px',
                            }}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                            {isExpanded ? '▼' : '▶'}
                        </button>
                    )}
                    {!hasChildren && <span style={{ width: '30px', display: 'inline-block' }}></span>}

                    {showCheckbox ? (
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1, userSelect: 'none' }}>
                            <input
                                type="checkbox"
                                checked={isChecked}
                                ref={input => {
                                    if (input) {
                                        input.indeterminate = isIndeterminateState;
                                    }
                                }}
                                onClick={(e) => handleCheckboxChange(node, parentPath, isThisGroupNode, e)}
                                onChange={() => {}} // Controlled by onClick
                                style={{ marginRight: '8px' }}
                            />
                            <span style={{ fontSize: '14px' }}>
                                {node.title}
                                {currentPath.length > 0 && (
                                    <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px' }}>
                                        ({currentPath.join(' > ')})
                                    </span>
                                )}
                            </span>
                        </label>
                    ) : (
                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#333', flex: 1 }}>
                            {node.title}
                        </span>
                    )}
                </div>

                {hasChildren && isExpanded && (
                    <div>
                        {node.children.map(child => renderNode(child, currentPath, level + 1, false))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <div style={{ marginBottom: '12px', padding: '8px', background: '#f8f9fa', borderRadius: '4px', fontSize: '13px', color: '#666' }}>
                💡 <strong>Tip:</strong> Use <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #ccc', borderRadius: '3px' }}>Ctrl</kbd> to toggle individual items,
                <kbd style={{ padding: '2px 6px', background: '#fff', border: '1px solid #ccc', borderRadius: '3px', marginLeft: '4px' }}>Shift</kbd> for range selection
            </div>
            <div style={{ maxHeight: '600px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '12px' }}>
                {tagsTree.map(node => renderNode(node, [], 0, true))}
            </div>
        </div>
    );
};

export default TagsTreeSelector;
